import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { FinishPlayAttemptInput } from '../../../shared/results';
import { LANE_RALLY_BONUS } from '../../../shared/laneRallyRules';
import { FullscreenGameShell } from '../../game/FullscreenGameShell';
import { RallyAudio } from './RallyAudio';
import { advanceDrive, createDrive, rallyResult, setPedal, startDrive, steerBy, steerLane, type DriveState } from './domain/driveModel';
import type { RallyConfig } from './domain/rallyLevels';
import type { CardAnchor, RallyFrame } from './three/LaneRallyController';
import type { PanelState } from './three/rallyTextures';
import './lane-rally.css';

type Controller = import('./three/LaneRallyController').LaneRallyController;
type Props = { config: RallyConfig; label?: string; restartEnabled?: boolean; onFinished?: (result: FinishPlayAttemptInput) => void };

const LETTERS = 'ABCD';
/** Kaydırma eşiği (px): bir parmak hareketinde birden fazla şerit geçilebilir. */
const SWIPE_STEP = 34;
/** Kaydırma göstergesi en az bu kadar görünür ve ilk şerit değişiminden sonra kaybolur. */
const HINT_MIN_MS = 2500;
/** Pedal bırakılıp araç bu kadar durursa GAZ düğmesi hatırlatma olarak parlar. */
const IDLE_HINT_MS = 1800;
const CARD_GAP = 8, CARD_MAX = 300, CARD_TOP = 10, CARD_MIN_HEIGHT = 56;

function snapshot(s: DriveState) { return { ...s, results: [...s.results], bonusEvents: s.bonusEvents.map((e) => [...e]), field: s.field.map((o) => ({ ...o })) }; }
function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const image = new Image(); const timer = window.setTimeout(() => { image.onload = null; image.onerror = null; reject(new Error('Görsel yüklenemedi.')); }, 15000);
    image.onload = () => { clearTimeout(timer); resolve(); }; image.onerror = () => { clearTimeout(timer); reject(new Error('Görsel yüklenemedi.')); }; image.src = url;
  });
}

function frameOf(s: DriveState, config: RallyConfig): RallyFrame {
  const q = config.questions[s.questionIndex];
  const answered = s.results.length > s.questionIndex ? s.results[s.questionIndex] : null;
  const panelStates: PanelState[] = q.choices.map((_, i) => answered
    ? (i === answered.correctChoiceIndex ? 'correct' : i === answered.selectedChoiceIndex ? 'wrong' : 'neutral')
    : (i === s.lane ? 'selected' : 'neutral'));
  return { phase: s.phase, clock: s.clock, distance: s.distance, speed: s.speed, x: s.x, laneCount: s.laneCount, lane: s.lane,
    gateRemaining: s.phase === 'approach' || s.phase === 'committed' ? s.gateDistance - s.distance : null, gateTotal: s.gateTotal,
    letters: q.choices.map((_, i) => LETTERS[i]), panelStates,
    objects: s.field.map((o) => ({ kind: o.kind, ahead: s.stretchStart + o.at - s.distance, x: o.x, takenAt: o.takenAt })),
    turbo: s.clock < s.turboUntil, skid: s.clock < s.skidUntil, obstacleAt: s.lastEvent?.kind === 'obstacle' ? s.lastEvent.at : null };
}

/** Kartlar tabela panellerinin ekran konumunu izler; uzaktayken okunaklı genişlikte ve üst üste binmeden dizilir. */
function layoutCards(anchors: CardAnchor[], width: number, height: number) {
  const n = anchors.length;
  const minWidth = Math.min(176, (width - 16 - CARD_GAP * (n - 1)) / n);
  const mean = anchors.reduce((sum, a) => sum + a.width, 0) / n;
  const cardWidth = Math.max(minWidth, Math.min(CARD_MAX, mean * 0.94));
  let centers = anchors.map((a) => a.x);
  if (n > 1 && centers[1] - centers[0] < cardWidth + CARD_GAP) {
    const middle = centers.reduce((sum, x) => sum + x, 0) / n;
    centers = centers.map((_, i) => middle + (i - (n - 1) / 2) * (cardWidth + CARD_GAP));
  }
  const left = centers[0] - cardWidth / 2, right = centers[n - 1] + cardWidth / 2;
  const shift = left < 8 ? 8 - left : right > width - 8 ? width - 8 - right : 0;
  // `y` kartın alt kenarıdır (kirişin üstü); kart üstten taşmaz.
  return anchors.map((a, i) => ({ x: centers[i] + shift, y: Math.max(CARD_TOP + CARD_MIN_HEIGHT, Math.min(height * 0.6, a.y - CARD_GAP)), width: cardWidth }));
}

export function LaneRallyGame({ config, label = 'Şerit Rallisi', restartEnabled = true, onFinished }: Props) {
  const stateRef = useRef<DriveState>(createDrive(config));
  const [view, setView] = useState(() => snapshot(stateRef.current));
  const sceneHost = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<Controller | null>(null);
  const audio = useRef(new RallyAudio()); const [muted, setMuted] = useState(audio.current.isMuted());
  const [mediaState, setMediaState] = useState<'loading' | 'ready' | 'error'>('loading'); const [loadAttempt, setLoadAttempt] = useState(0);
  const [sceneReady, setSceneReady] = useState(false); const [sceneError, setSceneError] = useState(false);
  const [paused, setPaused] = useState(false);
  const [hintDone, setHintDone] = useState(false); const [idle, setIdle] = useState(false);
  const cards = useRef<(HTMLButtonElement | null)[]>([]); const cardsHost = useRef<HTMLDivElement>(null);
  const speedRef = useRef<HTMLSpanElement>(null); const gateRef = useRef<HTMLSpanElement>(null); const progressRef = useRef<HTMLDivElement>(null);
  const sent = useRef(false); const onFinishedRef = useRef(onFinished); onFinishedRef.current = onFinished;
  const configRef = useRef(config); configRef.current = config;
  const pedalSources = useRef(new Set<string>());
  const startedAt = useRef(0); const idleSince = useRef<number | null>(null);
  const hintRef = useRef(false); hintRef.current = hintDone;
  const idleRef = useRef(false); idleRef.current = idle;
  const swipe = useRef<{ id: number; x: number } | null>(null);

  const question = config.questions[view.questionIndex];
  const fixed = question.choices.some((c) => c.label.length > 42 || Boolean(c.mediaUrl));
  const fixedRef = useRef(fixed); fixedRef.current = fixed;

  /** Kartları, hız göstergesini ve tabela mesafesini her karede DOM üzerinden günceller (React yeniden çizimi olmadan). */
  const placeCards = () => {
    const s = stateRef.current, host = sceneHost.current, controller = controllerRef.current;
    if (speedRef.current) speedRef.current.textContent = String(Math.round(s.speed * 3.6));
    if (gateRef.current) gateRef.current.textContent = s.phase === 'approach' || s.phase === 'committed' ? `${Math.max(0, Math.ceil(s.gateDistance - s.distance))} m` : '—';
    if (progressRef.current) progressRef.current.style.width = `${(s.results.length / configRef.current.questions.length) * 100}%`;
    if (!host || !cardsHost.current) return;
    const anchors = !fixedRef.current && s.phase === 'approach' ? controller?.cardAnchors() ?? null : null;
    cardsHost.current.classList.toggle('visible', fixedRef.current ? ['approach', 'committed'].includes(s.phase) : Boolean(anchors));
    if (!anchors) return;
    layoutCards(anchors, host.clientWidth, host.clientHeight).forEach((place, i) => {
      const card = cards.current[i]; if (!card) return;
      card.style.setProperty('--x', `${place.x}px`); card.style.setProperty('--y', `${place.y}px`); card.style.setProperty('--w', `${place.width}px`);
    });
  };

  const render = (dtMs: number) => {
    controllerRef.current?.render(frameOf(stateRef.current, configRef.current), dtMs / 1000);
    placeCards();
  };

  const advance = (ms: number) => {
    const s = stateRef.current; const revision = s.revision; const previousResults = s.results.length; const previousEvent = s.lastEvent; const previousPhase = s.phase;
    advanceDrive(s, configRef.current, ms);
    audio.current.drive(s.speed, s.pedal && !['ready', 'result'].includes(s.phase));
    render(ms);
    const playable = ['approach', 'committed', 'feedback', 'stretch'].includes(s.phase);
    if (playable && !s.pedal && s.speed === 0) { idleSince.current ??= s.clock; } else idleSince.current = null;
    const nextIdle = idleSince.current !== null && s.clock - idleSince.current >= IDLE_HINT_MS;
    if (nextIdle !== idleRef.current) setIdle(nextIdle);
    if (!hintRef.current && s.phase !== 'ready' && ((s.laneChanges > 0 && s.clock - startedAt.current >= HINT_MIN_MS) || s.results.length > 0)) setHintDone(true);
    if (s.revision !== revision) {
      setView(snapshot(s));
      if (s.lastEvent && s.lastEvent !== previousEvent) audio.current.play(s.lastEvent.kind);
      if (s.results.length > previousResults) audio.current.play(s.results.at(-1)!.correct ? 'correct' : 'wrong');
      if (s.phase === 'result' && previousPhase !== 'result' && !sent.current) {
        sent.current = true; audio.current.play('finish'); onFinishedRef.current?.(rallyResult(s, configRef.current));
      }
    }
  };

  const updatePedal = () => {
    const s = stateRef.current; const pressed = pedalSources.current.size > 0 && !document.hidden;
    if (s.pedal !== pressed) { setPedal(s, pressed); setView(snapshot(s)); }
  };
  const pedalOn = (source: string) => { audio.current.unlock(); pedalSources.current.add(source); updatePedal(); };
  const pedalOff = (source: string) => { pedalSources.current.delete(source); updatePedal(); };
  const releaseAll = () => { pedalSources.current.clear(); swipe.current = null; updatePedal(); };

  const steer = (action: () => boolean) => {
    if (document.hidden) return;
    if (action()) { audio.current.play('lane'); setView(snapshot(stateRef.current)); render(0); }
  };

  useEffect(() => {
    let active = true; setMediaState('loading');
    const urls = [...new Set(config.questions.flatMap((q) => [q.mediaUrl, ...q.choices.map((c) => c.mediaUrl)]).filter((url): url is string => Boolean(url)))];
    void Promise.all(urls.map(preloadImage)).then(() => { if (active) setMediaState('ready'); }, () => { if (active) setMediaState('error'); });
    return () => { active = false; };
  }, [config, loadAttempt]);

  useLayoutEffect(() => {
    let disposed = false;
    void import('./three/LaneRallyController').then(({ LaneRallyController }) => {
      if (disposed || !sceneHost.current) return;
      try {
        controllerRef.current = new LaneRallyController(sceneHost.current); setSceneReady(true); render(0);
      } catch { setSceneError(true); }
    }).catch(() => { if (!disposed) setSceneError(true); });
    const sound = audio.current;
    return () => { disposed = true; controllerRef.current?.dispose(); controllerRef.current = null; sound.dispose(); };
  }, []);

  useEffect(() => {
    let last = performance.now(), raf = 0;
    const loop = (now: number) => { const dt = now - last; last = now; if (!document.hidden && dt < 250) advance(dt); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    const visibility = () => { last = performance.now(); setPaused(document.hidden); if (document.hidden) { releaseAll(); audio.current.pause(); } else audio.current.resume(); };
    const blur = () => releaseAll();
    const isTyping = (target: EventTarget | null) => target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
    const keydown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.altKey || event.metaKey || isTyping(event.target)) return;
      const key = event.key.toLowerCase(); const s = stateRef.current;
      if (key === 'm' && !event.repeat) { setMuted(audio.current.toggle()); return; }
      if (s.phase === 'ready' || s.phase === 'result') return;
      if (['arrowup', 'w', ' '].includes(key)) { event.preventDefault(); if (!event.repeat) pedalOn('keyboard'); return; }
      if (event.repeat) return;
      if (['arrowleft', 'a'].includes(key)) { event.preventDefault(); steer(() => steerBy(stateRef.current, -1)); return; }
      if (['arrowright', 'd'].includes(key)) { event.preventDefault(); steer(() => steerBy(stateRef.current, 1)); return; }
      if (/^[1-4]$/.test(key)) { event.preventDefault(); steer(() => steerLane(stateRef.current, Number(key) - 1)); }
    };
    const keyup = (event: KeyboardEvent) => { if (['arrowup', 'w', ' '].includes(event.key.toLowerCase())) pedalOff('keyboard'); };
    document.addEventListener('visibilitychange', visibility); window.addEventListener('blur', blur);
    window.addEventListener('keydown', keydown); window.addEventListener('keyup', keyup);
    const debug = () => {
      const s = stateRef.current, c = configRef.current, all = s.bonusEvents.flat();
      return JSON.stringify({ template: 'lane-rally', phase: s.phase, clock: Math.round(s.clock), distance: Number(s.distance.toFixed(2)), speed: Number(s.speed.toFixed(2)),
        pedal: s.pedal, lane: s.lane, laneCount: s.laneCount, x: Number(s.x.toFixed(3)), questionIndex: s.questionIndex, totalQuestions: c.questions.length,
        gateRemaining: s.phase === 'approach' || s.phase === 'committed' ? Number((s.gateDistance - s.distance).toFixed(2)) : null, gateTotal: s.gateTotal,
        score: s.score, bonusPoints: s.bonusPoints, coins: all.filter((e) => e === 'coin').length, obstacleHits: all.filter((e) => e === 'obstacle').length,
        laneChanges: s.laneChanges, answered: s.results.length, results: s.results.map((r) => ({ level: r.level, selected: r.selectedChoiceIndex, correct: r.correct, durationMs: r.durationMs })),
        choices: c.questions[s.questionIndex].choices.map((ch, i) => ({ index: i, label: ch.label })),
        correctChoice: s.results.length > s.questionIndex ? s.results[s.questionIndex].correctChoiceIndex : null,
        objects: s.field.map((o) => ({ kind: o.kind, ahead: Number((s.stretchStart + o.at - s.distance).toFixed(1)), lane: o.lane, taken: o.takenAt !== null })),
        swipeHintVisible: !hintRef.current && s.phase !== 'ready' && s.phase !== 'result', pedalHint: idleRef.current });
    };
    window.render_game_to_text = debug; window.advanceTime = advance;
    return () => {
      cancelAnimationFrame(raf); document.removeEventListener('visibilitychange', visibility); window.removeEventListener('blur', blur);
      window.removeEventListener('keydown', keydown); window.removeEventListener('keyup', keyup);
      if (window.render_game_to_text === debug) { window.render_game_to_text = () => '{}'; window.advanceTime = () => undefined; }
    };
  }, []);

  const start = () => {
    if (mediaState !== 'ready' || !sceneReady) return;
    audio.current.unlock(); audio.current.play('start');
    startDrive(stateRef.current, config); startedAt.current = stateRef.current.clock;
    setView(snapshot(stateRef.current)); render(0);
  };
  const restart = () => {
    stateRef.current = createDrive(config); sent.current = false; releaseAll(); setHintDone(false); setIdle(false);
    setView(snapshot(stateRef.current)); render(0);
  };

  const onScenePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button') || !['approach', 'feedback', 'stretch'].includes(stateRef.current.phase) && stateRef.current.phase !== 'committed') return;
    audio.current.unlock(); swipe.current = { id: event.pointerId, x: event.clientX };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onScenePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const active = swipe.current; if (!active || active.id !== event.pointerId) return;
    const dx = event.clientX - active.x;
    if (Math.abs(dx) < SWIPE_STEP) return;
    active.x = event.clientX;
    steer(() => steerBy(stateRef.current, dx > 0 ? 1 : -1));
  };
  const onScenePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => { if (swipe.current?.id === event.pointerId) swipe.current = null; };

  const pedalDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault(); event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId); pedalOn(`pointer:${event.pointerId}`);
  };
  const pedalUp = (event: ReactPointerEvent<HTMLButtonElement>) => { pedalOff(`pointer:${event.pointerId}`); };

  const phase = view.phase;
  const playing = !['ready', 'result'].includes(phase);
  const approach = phase === 'approach' || phase === 'committed';
  const feedback = phase === 'feedback';
  const answered = view.results.length > view.questionIndex ? view.results[view.questionIndex] : null;
  const correctIndex = question.choices.findIndex((c) => c.correct);
  const coins = view.bonusEvents.flat().filter((e) => e === 'coin').length;
  const hits = view.bonusEvents.flat().filter((e) => e === 'obstacle').length;
  const recentEvent = view.lastEvent && view.clock - view.lastEvent.at < 1400 ? view.lastEvent.kind : null;
  const showHint = playing && !hintDone;
  const message = feedback ? (answered?.correct ? 'Doğru şerit! +100 · Turbo!' : 'Bu kez olmadı. Doğru cevabı öğren, yarış sürüyor.')
    : phase === 'committed' ? 'Şerit kilitlendi · Tabelanın altından geçiyorsun'
    : phase === 'stretch' ? (recentEvent === 'coin' ? `🪙 Altın! +${LANE_RALLY_BONUS.coinScore}` : recentEvent === 'obstacle' ? 'Bariyer! Biraz yavaşladın' : 'Altınları topla, bariyerlerden kaç!')
    : phase === 'arrival' ? 'Bitiş çizgisine son metreler!'
    : 'Doğru cevabın şeridine geç · Tabelaya vardığında bulunduğun şerit cevabın olur';

  return <FullscreenGameShell gameRef={controllerRef} className="rally-shell" toolbar={<>
    <strong className="rally-brand">ŞERİT <span>RALLİSİ</span></strong>
    <span className="rally-counter">{Math.min(view.questionIndex + 1, config.questions.length)} / {config.questions.length}</span>
    <span className="rally-score"><small>PUAN</small><b>{(view.score + view.bonusPoints).toLocaleString('tr-TR')}</b>
      <span className="rally-coins" aria-label={`${coins} altın`}>🪙 {coins}</span></span>
    <button type="button" className="rally-sound" onClick={() => { audio.current.unlock(); setMuted(audio.current.toggle()); }} aria-label={muted ? 'Sesi aç (M)' : 'Sesi kapat (M)'} aria-pressed={muted}>{muted ? '♪ Kapalı' : '♫ Açık'}</button>
  </>}>
    <div className={`rally-stage phase-${phase}${fixed ? ' fixed-answers' : ''}${recentEvent === 'obstacle' ? ' hit' : ''}`} aria-label={label}>
      <div className="rally-progress" aria-hidden="true"><div ref={progressRef} /></div>
      {playing && <header className="rally-question" key={question.id}>
        <span className="rally-question-number">{String(view.questionIndex + 1).padStart(2, '0')}<small>SORU</small></span>
        <div><span className="rally-eyebrow">DOĞRU ŞERİDİ SEÇ</span>{question.prompt && <h2>{question.prompt}</h2>}</div>
        {question.mediaUrl && <img src={question.mediaUrl} alt="Soru görseli" />}
      </header>}
      <div className={`rally-scene${playing ? ' drivable' : ''}`} onPointerDown={onScenePointerDown} onPointerMove={onScenePointerMove} onPointerUp={onScenePointerUp} onPointerCancel={onScenePointerUp}>
        <div className="rally-canvas" ref={sceneHost} aria-hidden="true" />
        {playing && !fixed && <div className="rally-cards" ref={cardsHost} role="group" aria-label="Cevap tabelaları">
          {question.choices.map((choice, i) => <button key={`${question.id}:${choice.id}`} type="button" ref={(el) => { cards.current[i] = el; }}
            className={`rally-card lane-${i}${i === view.lane ? ' selected' : ''}`} onClick={() => steer(() => steerLane(stateRef.current, i))} disabled={phase !== 'approach'}
            aria-label={`${LETTERS[i]} şeridine geç: ${choice.label || 'Görsel seçenek'}`}>
            <span className="rally-letter">{LETTERS[i]}</span><span className="rally-card-text">{choice.label}</span>
          </button>)}
        </div>}
        {playing && <div className="rally-hud" aria-hidden="true">
          <span className="rally-speed"><b ref={speedRef}>0</b><small>km/sa</small></span>
          {approach && <span className="rally-gate">Tabela <b ref={gateRef}>—</b></span>}
        </div>}
        {feedback && answered && <div className={`rally-banner ${answered.correct ? 'correct' : 'wrong'}`} role="status">
          <b>{answered.correct ? '✓ DOĞRU!' : '✕ YANLIŞ ŞERİT'}</b>
          <span>{answered.correct ? '+100 puan · Turbo' : `Doğru cevap: ${LETTERS[correctIndex]} · ${question.choices[correctIndex].label || 'Görsel seçenek'}`}</span>
          {!answered.correct && question.choices[correctIndex].mediaUrl && <img src={question.choices[correctIndex].mediaUrl} alt="Doğru cevabın görseli" />}
        </div>}
        {phase === 'stretch' && recentEvent && <div className={`rally-pop ${recentEvent}`} aria-hidden="true">{recentEvent === 'coin' ? `+${LANE_RALLY_BONUS.coinScore}` : 'Bariyer!'}</div>}
        {showHint && <div className="rally-swipe-hint" aria-hidden="true">
          <div className="rally-swipe-track"><span className="rally-swipe-arrow left">‹</span><span className="rally-swipe-hand">👆</span><span className="rally-swipe-arrow right">›</span></div>
          <p>Parmağını <b>sağa-sola kaydır</b>, şerit değiştir<small> · klavyede ← →</small></p>
        </div>}
        {playing && phase !== 'arrival' && <div className="rally-controls">
          <div className="rally-steer">
            <button type="button" onClick={() => steer(() => steerBy(stateRef.current, -1))} disabled={!['approach', 'feedback', 'stretch'].includes(phase) || view.lane === 0} aria-label="Sol şeride geç">◀</button>
            <button type="button" onClick={() => steer(() => steerBy(stateRef.current, 1))} disabled={!['approach', 'feedback', 'stretch'].includes(phase) || view.lane === view.laneCount - 1} aria-label="Sağ şeride geç">▶</button>
          </div>
          <button type="button" className={`rally-pedal${view.pedal ? ' pressed' : ''}${idle || (!hintDone && !view.pedal && view.speed === 0) ? ' nudge' : ''}`}
            onPointerDown={pedalDown} onPointerUp={pedalUp} onPointerCancel={pedalUp} onLostPointerCapture={pedalUp} onContextMenu={(event) => event.preventDefault()}
            onKeyDown={(event) => { if (event.key === 'Enter' && !event.repeat) pedalOn('pedal-key'); }} onKeyUp={(event) => { if (event.key === 'Enter') pedalOff('pedal-key'); }}
            aria-pressed={view.pedal} aria-label="Gaz pedalı: ilerlemek için basılı tut">
            <span>GAZ</span><small>{view.pedal ? 'basılı' : 'basılı tut'}</small>
          </button>
        </div>}
        {phase === 'ready' && <section className="rally-intro">
          <span className="rally-eyebrow">BİLGİNLE HIZLAN</span><h2>Şerit<br /><em>Rallisi</em></h2>
          <p>Cevaplar yolun üstündeki tabelalarda. Doğru cevabın şeridine geç, tabelanın altından geç!</p>
          <div className="rally-intro-chips"><span>{config.questions.length} soru</span><span>Can yok</span><span>🪙 Altın bonusu</span><span>Engeller elemez</span></div>
          <ul className="rally-how">
            <li><b className="key">GAZ</b><span>Basılı tut: araba ilerler. Bırakırsan durur, düşünebilirsin.</span></li>
            <li><b className="key">⇆</b><span>Parmağını kaydır ya da ◀ ▶ ile şerit değiştir.</span></li>
            <li><b className="key">A</b><span>Tabelaya vardığında bulunduğun şerit cevabın olur. Süre sınırı yok: gazı bırak, istediğin kadar düşün.</span></li>
          </ul>
          {mediaState === 'error' ? <><p role="alert">Soru görselleri yüklenemedi. Bağlantını kontrol edip yeniden dene.</p><button type="button" onClick={() => setLoadAttempt((n) => n + 1)}>Görselleri yeniden yükle</button></>
            : <button type="button" onClick={start} disabled={mediaState !== 'ready' || !sceneReady || sceneError}>{sceneError ? '3B sahne açılamadı' : mediaState === 'ready' && sceneReady ? 'Yarışa başla →' : 'Pist hazırlanıyor…'}</button>}
          <small>Klavye: ↑ / Boşluk basılı tut = gaz · ← → şerit · 1–4 doğrudan şerit</small>
        </section>}
        {phase === 'result' && <section className="rally-result">
          <span className="rally-eyebrow">YARIŞ TAMAMLANDI</span><h2>Bitiş çizgisini geçtin!</h2>
          <strong className="rally-final-score">{view.score + view.bonusPoints}<small> PUAN</small></strong>
          <p className="rally-bonus-line">Bilgi {view.score} · Altın bonusu +{view.bonusPoints} ({coins} altın, {hits} bariyer)</p>
          <p>{view.results.filter((r) => r.correct).length} doğru · {view.results.filter((r) => !r.correct).length} yanlış · {config.questions.length} tabela</p>
          {view.results.some((r) => !r.correct) && <details><summary>Yarış defteri · Doğru cevapları öğren</summary><div className="rally-mistakes">{view.results.filter((r) => !r.correct).map((r) => {
            const q = config.questions[r.level - 1], answer = q.choices[r.correctChoiceIndex];
            return <article key={r.level}><b>{r.level}. {q.prompt || 'Görsel soru'}</b>{q.mediaUrl && <img src={q.mediaUrl} alt="Soru görseli" />}<span>Doğru cevap: {LETTERS[r.correctChoiceIndex]} · {answer.label}</span>{answer.mediaUrl && <img src={answer.mediaUrl} alt="Doğru cevabın görseli" />}</article>;
          })}</div></details>}
          {restartEnabled && <button type="button" onClick={restart}>Yeniden yarış</button>}
        </section>}
      </div>
      {playing && fixed && <div className="rally-fixed-cards" ref={cardsHost} role="group" aria-label="Cevap tabelaları">
        {question.choices.map((choice, i) => <button key={`${question.id}:${choice.id}`} type="button"
          className={`rally-card lane-${i}${i === view.lane ? ' selected' : ''}${feedback && i === correctIndex ? ' correct' : ''}${feedback && answered && !answered.correct && i === answered.selectedChoiceIndex ? ' wrong' : ''}`}
          onClick={() => steer(() => steerLane(stateRef.current, i))} disabled={phase !== 'approach'} aria-label={`${LETTERS[i]} şeridine geç: ${choice.label || 'Görsel seçenek'}`}>
          <span className="rally-letter">{LETTERS[i]}</span>{choice.mediaUrl && <img src={choice.mediaUrl} alt={`${LETTERS[i]} seçeneğinin görseli`} />}{choice.label && <span className="rally-card-text">{choice.label}</span>}
        </button>)}
      </div>}
      {playing && <footer className={`rally-status ${feedback ? (answered?.correct ? 'correct' : 'wrong') : ''}`} role="status"><span>{message}</span>
        <small>{phase === 'stretch' ? `Altın +${LANE_RALLY_BONUS.coinScore} · Bariyer puan düşürmez` : 'GAZ basılı tut · Kaydır / ← → şerit değiştir'}</small></footer>}
      {paused && <div className="rally-paused">Yarış duraklatıldı</div>}
    </div>
  </FullscreenGameShell>;
}
